import { useEffect } from 'react';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import * as CookieConsent from 'vanilla-cookieconsent';

/**
 * Updates Google Consent Mode v2 based on cookie consent state.
 * Called on initial consent and on every preference change.
 */
function updateGtagConsent(cookie) {
  if (typeof window.gtag !== 'function') return;

  const analyticsGranted = cookie?.categories?.includes('analytics');
  const marketingGranted = cookie?.categories?.includes('marketing');

  window.gtag('consent', 'update', {
    analytics_storage: analyticsGranted ? 'granted' : 'denied',
    ad_storage: marketingGranted ? 'granted' : 'denied',
    ad_user_data: marketingGranted ? 'granted' : 'denied',
    ad_personalization: marketingGranted ? 'granted' : 'denied',
  });
}

/**
 * Loads the Mautic tracking script if marketing consent is granted.
 * Only loads once — subsequent calls are no-ops.
 */
function loadMautic() {
  if (window.MauticTrackingLoaded) return;
  window.MauticTrackingLoaded = true;

  (function (w, d, t, u, n, a, m) {
    w['MauticTrackingObject'] = n;
    w[n] = w[n] || function () { (w[n].q = w[n].q || []).push(arguments); };
    a = d.createElement(t);
    m = d.getElementsByTagName(t)[0];
    a.async = 1;
    a.src = u;
    m.parentNode.insertBefore(a, m);
  })(window, document, 'script', '/mtc.js', 'mt');
}

/**
 * Loads Clarity analytics if the REACT_APP_CLARITY_PROJECT_ID is configured.
 */
function loadClarity() {
  if (window.ClarityLoaded || window.ClarityLoading) return;
  const clarityId = process.env.REACT_APP_CLARITY_PROJECT_ID;
  if (!clarityId) return;

  window.ClarityLoading = true;
  (function(c,l,a,r,i,t,y){
    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
    t=l.createElement(r);
    t.async=1;
    t.src="https://www.clarity.ms/tag/"+i;
    t.onload=function(){
      window.ClarityLoaded = true;
      window.ClarityLoading = false;
    };
    t.onerror=function(){
      window.ClarityLoading = false;
      window.ClarityLoaded = false;
    };
    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
  })(window, document, "clarity", "script", clarityId);
}

function hasClarityConsent(cookie) {
  return cookie?.categories?.includes('analytics') || cookie?.categories?.includes('marketing');
}

/**
 * Loads Meta (Facebook) Pixel if REACT_APP_FACEBOOK_PIXEL_ID is configured.
 */
function loadMetaPixel() {
  if (window.MetaPixelLoaded) return;
  const pixelId = process.env.REACT_APP_FACEBOOK_PIXEL_ID;
  if (!pixelId) return;

  window.MetaPixelLoaded = true;
  (function() {
    var img=new Image();
    img.src="https://www.facebook.com/tr?id="+pixelId+"&ev=PageView&noscript=1";
  })();

  var script = document.createElement('script');
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  
  var fbq = (window.fbq = window.fbq || function() { (window.fbq.q = window.fbq.q || []).push(arguments); });
  fbq('init', pixelId);
  fbq('track', 'PageView');
}

/**
 * Loads Twitter (X) Pixel if REACT_APP_TWITTER_PIXEL_ID is configured.
 */
function loadTwitterPixel() {
  if (window.TwitterPixelLoaded) return;
  const pixelId = process.env.REACT_APP_TWITTER_PIXEL_ID;
  if (!pixelId) return;

  window.TwitterPixelLoaded = true;
  (function(w,d,t,r,u){
    var f,n;if(!w.twq){n=w.twq=function(){if(n.exe){n.exe.apply(n,arguments);}else{n.queue.push(arguments);}};
    n.version='1.1';n.queue=[];f=d.createElement(t);f.async=true;f.src=r;
    d.head.appendChild(f);}})(window,document,'script','https://static.ads-twitter.com/uwt.js');
  
  window.twq('init', pixelId);
  window.twq('track', 'PageView');
}

/**
 * Loads Reddit Pixel if REACT_APP_REDDIT_PIXEL_ID is configured.
 */
function loadRedditPixel() {
  if (window.RedditPixelLoaded) return;
  const pixelId = process.env.REACT_APP_REDDIT_PIXEL_ID;
  if (!pixelId) return;

  window.RedditPixelLoaded = true;
  (function() {
    var rdt = window.rdt = window.rdt || function() { (window.rdt.q = window.rdt.q || []).push(arguments); };
    rdt('init', pixelId);
    rdt('track', 'PageVisit');
  })();

  var script = document.createElement('script');
  script.src = 'https://www.redditstatic.com/ads/pixel.js';
  document.head.appendChild(script);
}

/**
 * Loads LinkedIn Insight Tag if REACT_APP_LINKEDIN_PARTNER_ID is configured.
 */
function loadLinkedInTag() {
  if (window.LinkedInLoaded) return;
  const partnerId = process.env.REACT_APP_LINKEDIN_PARTNER_ID;
  if (!partnerId) return;

  window.LinkedInLoaded = true;
  (function(){
    var _linkedin_partner_id = partnerId;
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(_linkedin_partner_id);
  })();

  var script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
  document.head.appendChild(script);

  var noscript = document.createElement('noscript');
  noscript.innerHTML = '<img height="1" width="1" style="display:none;" alt="" src="https://px.ads.linkedin.com/collect/?pid='+partnerId+'&conversionId=0&fmt=gif" />';
  document.head.appendChild(noscript);
}

/**
 * CookieConsentBanner — renders the vanilla-cookieconsent UI.
 * Initializes GTM Consent Mode v2 defaults, then runs the consent library.
 * 
 * Consent cookie is set on .veydra.io so it's shared across all subdomains
 * (app.veydra.io, docs.veydra.io, moodle-demo.veydra.io, etc.)
 */
function CookieConsentBanner() {
  useEffect(() => {
    CookieConsent.run({
      // Cookie settings — shared across *.veydra.io
      cookie: {
        name: 'veydra_consent',
        domain: '.veydra.io',
        expiresAfterDays: 395, // ~13 months, GDPR maximum
        sameSite: 'Lax',
      },

      // UI / behavior
      guiOptions: {
        consentModal: {
          layout: 'bar inline',
          position: 'bottom',
          equalWeightButtons: true,
          flipButtons: false,
        },
        preferencesModal: {
          layout: 'box',
          position: 'right',
          equalWeightButtons: true,
          flipButtons: false,
        },
      },

      // Categories
      categories: {
        necessary: {
          enabled: true,
          readOnly: true, // Cannot be disabled
        },
        functional: {
          enabled: true,
          readOnly: true, // Always on — required for payments and authenticated access
        },
        analytics: {
          enabled: false,
          autoClear: {
            cookies: [
              { name: /^_ga/ },       // GA4 cookies
              { name: '_gid' },
              { name: /^_gat/ },
              { name: /^mtc/ },       // Mautic cookies
              { name: 'mautic_device_id' },
              { name: 'mautic_session_id' },
            ],
          },
        },
        marketing: {
          enabled: false,
          autoClear: {
            cookies: [
              { name: /^mtc/ },                           // Mautic cookies
              { name: 'mautic_device_id' },
              { name: 'mautic_session_id' },
              { name: /^_fb/ },                           // Facebook Pixel
              { name: 'fr' },
              { name: /^twitter/ },                       // Twitter Pixel
              { name: /^twitter_id/ },
              { name: /^reddit/ },                        // Reddit Pixel
              { name: 'reddit_session' },
              { name: /^li_/ },                           // LinkedIn Insight
              { name: 'lidc' },
            ],
          },
        },
      },

      // Callbacks
      onFirstConsent: ({ cookie }) => {
        updateGtagConsent(cookie);
        if (hasClarityConsent(cookie)) {
          loadClarity();
        }
        if (cookie.categories.includes('marketing')) {
          loadMautic();
          loadMetaPixel();
          loadTwitterPixel();
          loadRedditPixel();
          loadLinkedInTag();
        }
      },

      onConsent: ({ cookie }) => {
        updateGtagConsent(cookie);
        if (hasClarityConsent(cookie)) {
          loadClarity();
        }
        if (cookie.categories.includes('marketing')) {
          loadMautic();
          loadMetaPixel();
          loadTwitterPixel();
          loadRedditPixel();
          loadLinkedInTag();
        }
      },

      onChange: ({ cookie }) => {
        updateGtagConsent(cookie);
        if (hasClarityConsent(cookie)) {
          loadClarity();
        }
        if (cookie.categories.includes('marketing')) {
          loadMautic();
          loadMetaPixel();
          loadTwitterPixel();
          loadRedditPixel();
          loadLinkedInTag();
        }
        // Dispatch event so other components (e.g. useMauticTracking) can react
        window.dispatchEvent(new CustomEvent('cookie-consent-changed', {
          detail: { categories: cookie.categories }
        }));
      },

      // Language / content
      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              title: 'We value your privacy',
              description:
                'We use cookies for core functionality, analytics, and personalization. <a href="/privacy" class="cc-link">Privacy Policy</a>',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              showPreferencesBtn: 'Manage preferences',
            },
            preferencesModal: {
              title: 'Cookie preferences',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              savePreferencesBtn: 'Save preferences',
              closeIconLabel: 'Close',
              sections: [
                {
                  title: 'How we use cookies',
                  description:
                    'We use cookies to ensure the basic functionality of the website and to enhance your experience. You can choose to opt in or out of each category whenever you want.',
                },
                {
                  title: 'Essential cookies',
                  description:
                    'These cookies are required for the platform to function. They handle authentication, security, and remembering your consent preferences. They cannot be disabled.',
                  linkedCategory: 'necessary',
                },
                {
                  title: 'Functional cookies',
                  description:
                    'These cookies are required to process payments and authenticate your access and subscription status. They are set by Stripe and our authentication service. They cannot be disabled.',
                  linkedCategory: 'functional',
                },
                {
                  title: 'Analytics cookies',
                  description:
                    'These cookies help us understand how visitors interact with our website by collecting anonymous usage data. This helps us improve the platform experience.',
                  linkedCategory: 'analytics',
                },
                {
                  title: 'Marketing cookies',
                  description:
                    'These cookies are used to track visitors and understand engagement patterns so we can provide relevant content and communications.',
                  linkedCategory: 'marketing',
                },
                {
                  title: 'More information',
                  description:
                    'For questions about our cookie policy, please <a href="/contact" class="cc-link">contact us</a>.',
                },
              ],
            },
          },
        },
      },
    });
  }, []);

  return null; // UI is rendered by the library into document.body
}

// Export the library's API so other components can trigger the preferences modal
export { CookieConsent };
export default CookieConsentBanner;
